import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useI18n } from "@/i18n/I18nContext";
import { filterMembers, distinctValues } from "@/lib/memberDerive";
import { readPeopleFilter, peopleQuery, peopleFilterCount, matchPerson, personContext, interestCounts, shortName, initialsOf, INTERESTS } from "@/lib/v3People";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, PrivacyNote } from "../components/kit";
import { V3Title, V3Actions, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { SearchField, FilterBar, Pill, Chip, ResultTable, ResultRow, V3Select } from "../components/v3app";
import { SegmentTabs } from "../components/v3people";

const useDirectory = () => useCore("/member/directory", (d) => requiredCollection(d, "members").filter((m) => m.id && m.name));

function PeopleList({ members, label }) {
  if (!members.length) return <p className="mt-[11px] rounded-[10px] border border-v3-line bg-white px-[16px] py-[16px] text-[14px] text-v3-muted" data-testid="member-directory-empty">Niemand gefunden. Passe Suche oder Filter an.</p>;
  return (
    <ResultTable testid="member-directory" label="Mitglieder">
      {members.map((m) => <ResultRow key={m.id} initial={initialsOf(m.name)} title={shortName(m.name)} context={personContext(m, label)} to={`/menschen/${encodeURIComponent(m.id)}`} testid={`member-directory-card-${m.id}`} />)}
    </ResultTable>
  );
}

function InterestList({ members, label, onPick }) {
  const counts = interestCounts(members);
  if (!counts.length) return <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="people-interests-empty">Noch keine Interessen angegeben.</p>;
  return (
    <ResultTable testid="people-interests" label="Interessen">
      {counts.map(([k, n]) => <ResultRow key={k} initial={label(k)[0]} title={label(k)} context={`${n} ${n === 1 ? "Person" : "Personen"}`} onOpen={() => onPick(k)} action="Anzeigen" testid={`people-interest-${k}`} />)}
    </ResultTable>
  );
}

// M08: members the Core lists as discoverable; search and filters only narrow that list, nothing else is searched.
export default function People() {
  const { t } = useI18n();
  const label = (k) => t(`member.categories.${k}`);
  const { search } = useLocation();
  const navigate = useNavigate();
  const f = readPeopleFilter(search);
  const [q, setQ] = useState(f.q);
  const [tab, setTab] = useState("people");
  const dir = useDirectory();
  const filtered = useMemo(() => filterMembers(dir.data || [], f), [dir.data, f.city, f.interest, f.language]); // eslint-disable-line react-hooks/exhaustive-deps
  const members = filtered.filter((m) => matchPerson(m, q));
  const n = peopleFilterCount(f);
  return (
    <div data-m="M08" data-testid="people-page">
      <V3Title title="Menschen entdecken" intro="Entdecke Menschen über gemeinsame Interessen und freiwillige Ortsangaben." />
      <SearchField id="people-q" label="Mitglieder durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={q} onChange={setQ} testid="member-directory-search" />
      <SegmentTabs label="Ansicht" testid="people-tab" value={tab} onChange={setTab} items={[["people", "Menschen"], ["interests", "Interessen"]]} />
      {n > 0 && <FilterBar testid="people-active" end={<Link to="/menschen" data-testid="people-filter-reset" className="inline-flex min-h-[44px] items-center px-[12px] text-[13px] font-bold text-v3-magenta hover:underline">Zurücksetzen</Link>}>
        <Pill tone="ink" testid="people-active-count">{n} {n === 1 ? "Filter" : "Filter"} aktiv</Pill></FilterBar>}
      <div className="mt-[8px]" role="region" aria-live="polite" aria-label={`${members.length} Personen`}>
        {dir.error ? <ConnectionError onRetry={dir.reload} /> : !dir.data ? <Loading />
          : tab === "people" ? <PeopleList members={members} label={label} />
          : <InterestList members={filtered} label={label} onPick={(k) => { setTab("people"); navigate(`/menschen${peopleQuery({ ...f, interest: k, q })}`); }} />}
      </div>
      <V3Actions className="mt-[21px]"><Link to={`/menschen/filtern${peopleQuery({ ...f, q })}`} data-testid="people-filter-open" className={V3_PRIMARY}>Filter anpassen</Link></V3Actions>
      <div className="mt-[24px]"><PrivacyNote>Es gibt bewusst keine Filter nach Diagnose, Behinderung oder Aussehen. Gezeigt wird nur die Stadt, nie ein genauer Ort.</PrivacyNote></div>
    </div>
  );
}

// M50 – the choice is written into the /menschen URL; nothing is stored.
export function PeopleFilter() {
  const { t } = useI18n();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dir = useDirectory();
  const [draft, setDraft] = useState(() => readPeopleFilter(search));
  const [open, setOpen] = useState("");
  const toggle = (k) => setOpen(open === k ? "" : k);
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const cities = distinctValues(dir.data || [], (m) => m.city);
  const langs = distinctValues(dir.data || [], (m) => m.languages);
  const n = peopleFilterCount(draft);
  return (
    <div data-m="M50" data-testid="people-filter-page">
      <V3Title title="Menschen filtern" intro="Suche über Interessen und grobe Ortsangaben." />
      <SearchField id="pf-q" label="Mitglieder durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={draft.q} onChange={(q) => setDraft({ ...draft, q })} testid="people-filter-q" />
      <FilterBar testid="people-filter-bar" onReset={() => setDraft({ city: "", interest: "", language: "", q: "" })} canReset={n > 0 || !!draft.q}><Pill tone="ink">Ausgewählte Filter</Pill></FilterBar>
      {dir.error ? <ConnectionError onRetry={dir.reload} /> : !dir.data ? <Loading /> : (
        <ResultTable testid="people-filter-table" label="Filter">
          <ResultRow initial="O" title="Ort" context={draft.city || "Alle Orte"} onOpen={() => toggle("city")} expanded={open === "city"} testid="people-filter-city-row">
            {open === "city" && <div className="mt-[11px] rounded-[10px] border border-v3-line bg-white px-[16px] pt-[16px]"><V3Select id="pf-city" label="Stadt" value={draft.city} onChange={set("city")} testid="people-filter-city">
              <option value="">Alle Orte</option>{cities.map((c) => <option key={c} value={c}>{c}</option>)}</V3Select></div>}
          </ResultRow>
          <ResultRow initial="I" title="Interessen" context={draft.interest ? t(`member.categories.${draft.interest}`) : "Alle Interessen"} onOpen={() => toggle("interest")} expanded={open === "interest"} testid="people-filter-interest-row">
            {open === "interest" && <div className="mt-[11px] flex flex-wrap gap-2 rounded-[10px] border border-v3-line bg-white px-[16px] py-[16px]" role="group" aria-label="Interesse wählen">
              {INTERESTS.map((k) => <Chip key={k} pressed={draft.interest === k} onClick={() => setDraft({ ...draft, interest: draft.interest === k ? "" : k })} testid={`people-filter-interest-${k}`}>{t(`member.categories.${k}`)}</Chip>)}</div>}
          </ResultRow>
          <ResultRow initial="S" title="Sprachen" context={draft.language || "Alle Sprachen"} onOpen={() => toggle("language")} expanded={open === "language"} testid="people-filter-language-row">
            {open === "language" && <div className="mt-[11px] rounded-[10px] border border-v3-line bg-white px-[16px] pt-[16px]"><V3Select id="pf-lang" label="Sprache" value={draft.language} onChange={set("language")} testid="people-filter-language">
              <option value="">Alle Sprachen</option>{langs.map((l) => <option key={l} value={l}>{l}</option>)}</V3Select></div>}
          </ResultRow>
          <ResultRow initial="L" title="Weitere Länder" context="Noch nicht verfügbar" onOpen={() => toggle("country")} expanded={open === "country"} testid="people-filter-country-row">
            {open === "country" && <div className="mt-[11px]"><Unavailable testid="people-filter-country-note">Der gemeinsame Core kennt noch kein Land je Mitglied. Deutschland und Österreich lassen sich darum noch nicht bewusst einschliessen.</Unavailable></div>}
          </ResultRow>
        </ResultTable>
      )}
      <V3Actions className="mt-[21px]">
        <button type="button" onClick={() => navigate(`/menschen${peopleQuery(draft)}`)} data-testid="people-filter-apply" className={V3_PRIMARY}>Filter anwenden</button>
        <Link to={`/menschen${search}`} data-testid="people-filter-cancel" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </div>
  );
}
